import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FloatingContactButton } from "@/components/FloatingContactButton";
import { CheckCircle, Star, Shield, Truck, Award, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EssaiElitePrecision = () => {
  const navigate = useNavigate();
  const [customerType, setCustomerType] = useState('particulier');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postalCode: '',
    region: '',
    neighborhood: '',
    city: '',
    address: '',
    streetNumber: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Redirection vers le lien externe pour finaliser la commande
    window.open('https://outilmatpro-disque-universel-elitepro.systeme.io/essai-elite-precision-d19750d1', '_blank');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: "1. Est-ce vraiment sans engagement ?",
      answer: "Oui, totalement. Vous pouvez annuler votre essai ou votre abonnement à tout moment, sans justification, directement depuis votre espace client ou en nous contactant. Aucune durée minimale n'est imposée."
    },
    {
      question: "2. Que se passe-t-il à la fin de la période d'essai ?",
      answer: "À l'issue de votre essai gratuit de 30 jours, vous recevez automatiquement un disque ElitePrecision neuf chaque mois au tarif préférentiel de 49,90 € HT (au lieu de 69,90 € HT). Vous pouvez résilier votre abonnement à tout moment avant ou après l'envoi du disque."
    },
    {
      question: "3. Le disque est-il compatible avec ma meuleuse ?",
      answer: "Oui, le disque ElitePrecision est compatible avec la grande majorité des meuleuses standards, qu'elles soient filaires ou à batterie, en diamètre 125 mm avec un alésage de 22,23 mm, norme universelle du secteur. Pour une performance optimale, nous recommandons une meuleuse d'au moins 1200 watts de puissance. Notre équipe technique est disponible pour vous conseiller si besoin."
    },
    {
      question: "4. Est-ce réservé uniquement aux professionnels ?",
      answer: "Oui, cette offre est exclusivement réservée aux professionnels du bâtiment, et en particulier aux carreleurs exigeants. Elle n'est pas accessible aux particuliers."
    },
    {
      question: "5. Que puis-je couper avec le disque ElitePrecision ?",
      answer: "ElitePrecision est conçu pour une performance maximale sur : Grès cérame pleine masse, Carreaux grand format, Pierre naturelle, ardoise, granit, marbre. Il assure une coupe nette, sans éclats, même sur les matériaux les plus durs."
    },
    {
      question: "6. Comment annuler mon abonnement ?",
      answer: "Connectez-vous à votre espace client via le lien reçu par email. En un clic, vous pouvez suspendre ou résilier votre abonnement. Vous pouvez aussi nous contacter à contact@outilmatpro.fr."
    },
    {
      question: "7. Puis-je recommander l'essai plus tard ?",
      answer: "Non. Cette offre d'essai est limitée à une seule fois par client. Nous vous recommandons d'en profiter maintenant, tant que l'offre est disponible."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header avec logo */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Retour</span>
              </Button>
              <div className="flex items-center space-x-3">
                <img 
                  src="./images/Logo web outilmatpro.jpg" 
                  alt="OutilMatPro Logo" 
                  className="h-12 w-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-green-700 mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
              Testez gratuitement le disque ElitePrecision et faites la différence dès votre prochain chantier
            </h1>
            <p className="text-lg text-gray-600 italic mb-8">
              Conçu pour les carreleurs pros. Coupez plus proprement. Gagnez du temps. Offrez une finition irréprochable à vos clients.
            </p>
          </div>

          {/* Image produit */}
          <div className="text-center mb-12">
            <img 
              src="./images/disque_elite_precision_1.jpeg" 
              alt="Disque Elite Precision" 
              className="mx-auto h-48 w-auto rounded-lg shadow-lg"
            />
          </div>

          {/* Avantages principaux */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Essai - Disque Elite précision</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Coupe nette, sans éclats, même sur grès cérame pleine masse</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Zéro chauffe, pas de voilage – affûtage automatique</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Garantie à vie contre le voilage</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">SAV prioritaire</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Résiliable à tout moment</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Mètre ruban inox offert (valeur 18 €) jusqu'à vendredi</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-12">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Prix habituel</strong> : <del>69,90 € HT</del></span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Essai gratuit de 30 jours</strong></span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Tarif post-essai</strong> : 49,90 € HT/mois</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Sans engagement</strong></span>
                </div>
              </div>
            </div>
          </div>

          {/* Important */}
          <div className="bg-orange-100 border-l-4 border-orange-500 p-6 mb-12">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Important</h3>
            <p className="text-gray-700">
              Vous ne payez aujourd'hui que les frais de traitement logistique de 11,99 € HT (soit 14,39 € TTC). 
              À l'issue de votre essai gratuit de 30 jours, un nouveau disque ElitePrecision vous sera automatiquement 
              expédié chaque mois au tarif préférentiel de 49,90 € HT (au lieu de 69,90 € HT). 
              Offre sans engagement, résiliable à tout moment. En finalisant votre commande, vous confirmez avoir lu et accepté les conditions générales de vente.
            </p>
          </div>

          {/* Formulaire de commande */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-green-700">
                Commandez votre essai Elite Precision
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Type de client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Type de client</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="customer-type"
                        value="particulier"
                        checked={customerType === 'particulier'}
                        onChange={(e) => setCustomerType(e.target.value)}
                        className="mr-2"
                      />
                      Particulier
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="customer-type"
                        value="entreprise"
                        checked={customerType === 'entreprise'}
                        onChange={(e) => setCustomerType(e.target.value)}
                        className="mr-2"
                      />
                      Entreprise
                    </label>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      placeholder="Prénom"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Nom"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="email"
                      placeholder="Adresse email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      placeholder="Numéro de téléphone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Input
                      placeholder="Code postal"
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Région"
                      value={formData.region}
                      onChange={(e) => handleInputChange('region', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Quartier"
                      value={formData.neighborhood}
                      onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Input
                      placeholder="Ville"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Adresse"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Numéro de rue"
                      value={formData.streetNumber}
                      onChange={(e) => handleInputChange('streetNumber', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Mode de paiement */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-bold text-lg mb-4 text-gray-800">Mode de paiement</h4>
                  <div className="flex items-center space-x-3 mb-4">
                    <input type="radio" name="payment" value="card" defaultChecked className="mr-2" />
                    <span>Carte de crédit</span>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <Input placeholder="1234 1234 1234 1234" />
                    <Input placeholder="MM / AA" />
                    <Input placeholder="CVC" />
                  </div>
                </div>

                {/* Prix */}
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Frais de livraison & Emballage</span>
                    <span className="font-bold">11,99 €</span>
                  </div>
                  <div className="text-sm text-gray-600 text-right">HT</div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-4 text-xl font-bold"
                  style={{fontFamily: 'Outfit, sans-serif'}}
                >
                  Je commence mon essai gratuit
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Vous ne payez aujourd'hui que les frais de traitement logistique de 11,99 € HT (soit 14,39 € TTC). 
                  À l'issue de votre essai gratuit de 30 jours, un nouveau disque ElitePrecision vous sera automatiquement 
                  expédié chaque mois au tarif préférentiel de 49,90 € HT (au lieu de 69,90 € HT). 
                  Offre sans engagement, résiliable à tout moment. En finalisant votre commande, vous confirmez avoir lu et accepté les conditions générales de vente.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <div className="mb-12">
            <div className="bg-green-600 text-white p-6 rounded-t-lg">
              <h3 className="text-2xl font-bold text-center">FAQ</h3>
            </div>
            <div className="bg-green-600 text-white">
              {faqItems.map((item, index) => (
                <div key={index} className="border-b border-green-500 last:border-b-0">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 text-left flex justify-between items-center hover:bg-green-700 transition-colors"
                  >
                    <h4 className="font-bold text-lg text-white">{item.question}</h4>
                    {openFaq === index ? <ChevronUp className="text-white" /> : <ChevronDown className="text-white" />}
                  </button>
                  
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-white leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <FloatingContactButton />
    </div>
  );
};

export default EssaiElitePrecision;